// B2CProfilePage.tsx
import React, { useState } from "react";
import {
  Box, Text, VStack, HStack, Heading, Divider,
  useColorModeValue, Grid, GridItem, Icon,
  Button, Input, FormControl, FormLabel,
  useToast,
} from "@chakra-ui/react";
import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import axios from "axios";
import { useStudentContext } from "../B2CStudentContext";

const InfoRow = ({ icon, label, value }: { icon: any; label: string; value: string }) => {
  const mutedText = useColorModeValue("gray.500", "gray.400");
  return (
    <HStack spacing={3} align="start">
      <Icon as={icon} color={mutedText} mt={1} flexShrink={0} />
      <Box>
        <Text fontSize="xs" color={mutedText}>{label}</Text>
        <Text fontSize="sm" fontWeight="medium">{value || "—"}</Text>
      </Box>
    </HStack>
  );
};

const B2CProfilePage = () => {
  const { studentContext } = useStudentContext();
  const student = studentContext?.student_info;

  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const toast = useToast();
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("b2c_token");

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords do not match", status: "error", duration: 3000 });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: "Password must be at least 8 characters", status: "error", duration: 3000 });
      return;
    }
    setPwLoading(true);
    try {
      await axios.put(
        `${apiUrl}/b2c/student/change-password`,
        { old_password: currentPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Password updated successfully", status: "success", duration: 3000 });
      setChangingPassword(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      toast({
        title: error.response?.data?.detail ?? "Failed to update password",
        status: "error",
        duration: 3000,
      });
    } finally {
      setPwLoading(false);
    }
  };

  if (!student) {
    return (
      <Box p={8} textAlign="center" color={mutedText}>
        <Text>Profile information not available.</Text>
      </Box>
    );
  }

  return (
    <Box maxW="700px" mx="auto" p={6}>
      <VStack align="start" spacing={1} mb={8}>
        <Heading size="lg">Profile & Settings</Heading>
        <Text color={mutedText}>Your account information</Text>
      </VStack>

      {/* Personal details */}
      <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6} mb={5}>
        <HStack mb={4}>
          <Icon as={FaUser} />
          <Text fontWeight="bold">Personal Details</Text>
        </HStack>
        <Divider mb={4} />
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
          <GridItem>
            <InfoRow icon={FaUser} label="First Name" value={student.first_name} />
          </GridItem>
          <GridItem>
            <InfoRow icon={FaUser} label="Last Name" value={student.last_name ?? ""} />
          </GridItem>
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <InfoRow icon={FaEnvelope} label="Email" value={student.email} />
          </GridItem>
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <InfoRow icon={FaPhone} label="Contact Number" value={student.contact_no} />
          </GridItem>
        </Grid>
      </Box>

      {/* Address */}
      <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6} mb={5}>
        <HStack mb={4}>
          <Icon as={FaMapMarkerAlt} />
          <Text fontWeight="bold">Address</Text>
        </HStack>
        <Divider mb={4} />
        <VStack align="start" spacing={3}>
          <InfoRow
            icon={FaMapMarkerAlt}
            label="Street Address"
            value={student.street_address}
          />
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} gap={4} w="100%">
            <GridItem>
              <InfoRow icon={FaMapMarkerAlt} label="Suburb" value={student.suburb} />
            </GridItem>
            <GridItem>
              <InfoRow icon={FaMapMarkerAlt} label="Postcode" value={student.postcode} />
            </GridItem>
            <GridItem>
              <InfoRow icon={FaMapMarkerAlt} label="State" value={student.state} />
            </GridItem>
          </Grid>
          <InfoRow icon={FaMapMarkerAlt} label="Country" value={student.country} />
        </VStack>
      </Box>

      {/* Password */}
      <Box bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} p={6}>
        <HStack justify="space-between" mb={4}>
          <Text fontWeight="bold">Password</Text>
          {!changingPassword && (
            <Button size="sm" variant="outline" onClick={() => setChangingPassword(true)}>
              Change Password
            </Button>
          )}
        </HStack>

        {changingPassword && (
          <>
            <Divider mb={4} />
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Current Password</FormLabel>
                <Input type="password" value={currentPassword} size="sm"
                  onChange={e => setCurrentPassword(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">New Password</FormLabel>
                <Input type="password" value={newPassword} size="sm"
                  onChange={e => setNewPassword(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">Confirm New Password</FormLabel>
                <Input type="password" value={confirmPassword} size="sm"
                  onChange={e => setConfirmPassword(e.target.value)} />
              </FormControl>
              <HStack justify="flex-end" spacing={3}>
                <Button size="sm" variant="ghost"
                  onClick={() => {
                    setChangingPassword(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}>
                  Cancel
                </Button>
                <Button size="sm" colorScheme="blue"
                  isLoading={pwLoading}
                  onClick={handlePasswordChange}>
                  Update Password
                </Button>
              </HStack>
            </VStack>
          </>
        )}
      </Box>
    </Box>
  );
};

export default B2CProfilePage;